import { useCallback, useEffect, useMemo } from "react";
import { Route } from "nextjs-routes";
import { extractFirstQueryParam } from "domains/commons/misc";
import useRouter from "domains/navigation/hooks/useRouter";
import _ from "lodash";

export interface TabItem {
  label: string;
  link?: Route;
}

export interface Tabs<T extends string = string> {
  ids: T[];
  items: {
    [id in T]: TabItem | string;
  };
}

export const createTabs = <T extends string>(tabs: Tabs<T>): Tabs<T> => {
  const normalizedItems = Object.entries(tabs.items).reduce<
    Record<string, TabItem>
  >((acc, [key, value]) => {
    acc[key] =
      typeof value === "string" ? { label: value } : (value as TabItem);
    return acc;
  }, {});

  return {
    ids: tabs.ids,
    items: normalizedItems as { [id in T]: TabItem },
  };
};

const normalizeTabItem = (value: TabItem | string): TabItem =>
  typeof value === "string" ? { label: value } : value;

export const useTabs = <T extends string>(tabs: Tabs<T>, defaultTabId?: T) => {
  const router = useRouter();
  const actualDefaultTabId = defaultTabId ?? tabs.ids[0];

  const { tabId, tabIndex } = useMemo(() => {
    if (!router.isReady) {
      return { tabId: undefined, tabIndex: undefined };
    }

    const currentTab = extractFirstQueryParam(router.query.tab);
    const foundTabId = _.findKey(tabs.items, (val) => {
      const item = normalizeTabItem(val);
      if (!item.link) {
        return currentTab === item.label;
      }

      // When no tab is provided, we match only the pathname
      if (!router.query.tab) {
        return item.link.pathname === router.pathname;
      }

      return (
        item.link.pathname === router.pathname &&
        item.link.query?.tab === currentTab
      );
    }) as T | undefined;

    const resolvedTabId = foundTabId || actualDefaultTabId;
    const resolvedIndex = tabs.ids.indexOf(resolvedTabId);

    return {
      tabId: resolvedTabId,
      tabIndex: resolvedIndex >= 0 ? resolvedIndex : 0,
    };
  }, [
    router.isReady,
    router.pathname,
    router.query.tab,
    tabs,
    actualDefaultTabId,
  ]);

  const onTabChange = useCallback(
    (index: number) => {
      const newTabId = tabs.ids[index];
      const item = normalizeTabItem(tabs.items[newTabId]);

      if (item.link) {
        const query = { ...router.query, ...item.link.query } as Record<
          string,
          string
        >;
        if (query.tab === "") {
          delete query.tab;
        }
        void router.push({
          pathname: item.link.pathname,
          query,
        });
        return;
      }

      const query = { ...router.query, tab: item.label } as Record<
        string,
        string
      >;
      if (query.tab === "") {
        delete query.tab;
      }
      void router.push({
        pathname: router.pathname,
        query,
      });
    },
    [router, tabs]
  );

  useEffect(() => {
    if (!router.isReady || tabIndex !== undefined) return;

    const defaultItem = normalizeTabItem(tabs.items[actualDefaultTabId]);
    if (defaultItem.link) {
      const query = { ...router.query, ...defaultItem.link.query } as Record<
        string,
        string
      >;
      if (query.tab === "") {
        delete query.tab;
      }
      void router.push({
        pathname: defaultItem.link.pathname,
        query,
      });
      return;
    }

    const query = { ...router.query, tab: defaultItem.label } as Record<
      string,
      string
    >;
    if (query.tab === "") {
      delete query.tab;
    }
    void router.push({
      pathname: router.pathname,
      query,
    });
  }, [router, tabs, tabIndex, actualDefaultTabId]);

  return { tabId, tabIndex: tabIndex ?? 0, onTabChange };
};
